import { defineStore } from 'pinia';
import type { MigrationReplication, MigrationReplicationAdd } from '@schema';
import api from '@/common/utils/api';
import useCommonStore, { type FetchOptions } from '@/common/store/pinia/common';
import mapper, { MappedReplication } from '@/common/store/pinia/replications/replicationsMapper';
import { getMigrationsUrl, getReplicationsUrl } from '@/common/store/pinia/maasUrls';
import CancellationError from '@/common/utils/errors/CancellationError';
import { abortByKey } from '@/common/utils/takeLastByKey';
import { takeLastByKeyIgnoreCancel } from '@/common/utils/takeLastByKeyIgnoreCancel';
import { ReplicationStatus } from '@/common/constants/replication.constants';
import { useMigrationTasksStore } from '@/common/store/pinia/migrationTasks';
import { notifyReplicationStatusChange } from '@/common/store/pinia/replications/replicationNotifications';
import { isNotificationForCurrentProject } from '@/common/utils/notifications/isNotificationForCurrentProject';

export interface CreateReplicationOptions {
  shutdownInstances?: boolean;
  autoDeploy?: boolean;
  deploymentName?: string;
}

export const useReplicationsStore = defineStore('replications', () => {
  const migrationTasksStore = useMigrationTasksStore();

  const commonStore = useCommonStore<MigrationReplication, MappedReplication>({
    getUrl: getMigrationsUrl,
    itemKey: 'id',
    plural: (data) => (data as { data?: MigrationReplication[] })?.data ?? [],
    mapItem: mapper,
    updateMethod: 'patch',
    dataChangeMode: 'direct'
  })();

  const {
    getItemById,
    getItemsByView,
    addViewList,
    setData,
    removeItem: removeItemBase,
    pushData
  } = commonStore;

  const removeItem = (id: ID) => {
    migrationTasksStore.removeTasksForParent(id as string);
    removeItemBase(id);
  };

  const fetchItem = (
    migrationId: string,
    replicationId: string,
    options: FetchOptions = {}
  ) => {
    const view = `migration-${migrationId}`;
    return api.get<MigrationReplication>(
      `${getReplicationsUrl(migrationId)}/${replicationId}`,
      undefined,
      { abortController: options.abortController }
    )
      .then((response) => {
        migrationTasksStore.setTasksForParent(response.data, 'replication', migrationId);
        pushData([response.data], view);
      });
  };

  const fetchItems = (migrationId?: string, options: FetchOptions = {}) => {
    if (!migrationId) {
      return Promise.resolve({ data: [] as MappedReplication[] });
    }
    const view = `migration-${migrationId}`;
    addViewList(view);
    return api.get<{ data: MigrationReplication[] }>(
      getReplicationsUrl(migrationId),
      undefined,
      { abortController: options.abortController }
    )
      .then((response) => {
        response.data.data.forEach(
          replication => migrationTasksStore.setTasksForParent(replication, 'replication', migrationId)
        );
        setData(response.data.data, view);
      })
      .catch((error) => {
        if (error instanceof CancellationError) {
          throw error;
        }
        setData([], view);
      })
      .then(() => ({ data: getItemsByView.value(view) }));
  };

  const fetchItemsLatest = (migrationId: string) => takeLastByKeyIgnoreCancel(
    abortController => fetchItems(migrationId, { abortController }),
    getReplicationsUrl(migrationId)
  );

  const cancelFetchItems = (migrationId: string) => {
    abortByKey(getReplicationsUrl(migrationId));
  };

  const createReplication = (migrationId: string, options?: CreateReplicationOptions) => {
    const view = `migration-${migrationId}`;
    const body: MigrationReplicationAdd = {
      shutdown_instances: options?.shutdownInstances ?? false,
      auto_deploy: options?.autoDeploy ?? false,
      ...(options?.autoDeploy && options?.deploymentName
        ? { deployment_name: options.deploymentName }
        : {})
    };
    return api.post<MigrationReplication>(getReplicationsUrl(migrationId), body)
      .then((response) => {
        migrationTasksStore.setTasksForParent(response.data, 'replication', migrationId);
        pushData([response.data], view);
        return { data: getItemById.value(response.data.id) };
      });
  };

  const cancelReplication = (migrationId: string, id: string) => api.post(`${getReplicationsUrl(migrationId)}/${id}/cancel`)
    .then(() => fetchItems(migrationId));

  const deleteReplication = (migrationId: string, id: string) => api.delete(`${getReplicationsUrl(migrationId)}/${id}`)
    .then(() => {
      removeItem(id);
    });

  const deleteItems = (migrationId: string, ids: string[]) => Promise.all(ids.map(id => deleteReplication(migrationId, id)));

  const fetchItemLatest = (migrationId: string, replicationId: string) => takeLastByKeyIgnoreCancel(
    abortController => fetchItem(migrationId, replicationId, { abortController }),
    `${getReplicationsUrl(migrationId)}/${replicationId}`
  );

  const onCompute = (data: SocketEvent<{
    id?: string;
    migration_id?: string;
    project_id?: string;
    status?: ReplicationStatus;
    index?: number;
  }>) => {
    if (!data?.object) return;
    if (!isNotificationForCurrentProject(data.object.project_id)) return;

    if (data.type !== 'migration_replications') return;

    const { id, migration_id: migrationId, status, index } = data.object;
    if (!id || !migrationId) return;

    if (data.operation === 'deleted') {
      removeItem(id);
      return;
    }
    fetchItemLatest(migrationId, id);

    const replicationIndex = index ?? 0;
    notifyReplicationStatusChange(status, id, replicationIndex);
  };

  return {
    ...commonStore,
    removeItem,
    fetchItem,
    fetchItemLatest,
    fetchItems,
    fetchItemsLatest,
    cancelFetchItems,
    createReplication,
    cancelReplication,
    deleteReplication,
    deleteItems,
    onCompute
  };
}, {
  ws: {
    compute: 'onCompute'
  }
});

export default useReplicationsStore;
